/**
 * HiBFF Schools — Outreach Tracker.
 *
 * CRM-style mayor outreach pipeline ingested from the master spreadsheet
 * (60 cities × 4 census regions). Lives inside the Schools Command Centre
 * as a new tab. Aggregate-only — no individual teen data touched here.
 *
 * Pipeline stages (matches backend STATUS_STAGES):
 *   not_contacted → letter_sent → enquiry_received → in_discussion → active → declined
 *
 * Priority colour-coding (per the source spreadsheet's "How To Use" sheet):
 *   ★★★ Top priority         — green
 *   ★★  Outreach target      — grey
 *   ★   Paying subscriber    — blue
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, X, Save, ExternalLink, Send, FileText, Inbox, Clock } from 'lucide-react';
import { apiClient } from '../../api/client';

const STAGES = [
  { value: 'not_contacted',      label: 'Not Contacted',     pillClass: 'bg-white/10 text-white/70' },
  { value: 'letter_sent',        label: 'Letter Sent',       pillClass: 'bg-amber-500/20 text-amber-300' },
  { value: 'enquiry_received',   label: 'Enquiry Received',  pillClass: 'bg-sky-500/20 text-sky-300' },
  { value: 'in_discussion',      label: 'In Discussion',     pillClass: 'bg-violet-500/20 text-violet-300' },
  { value: 'active',             label: 'Active',            pillClass: 'bg-emerald-500/20 text-emerald-300' },
  { value: 'declined',           label: 'Declined',          pillClass: 'bg-rose-500/20 text-rose-300' },
];

const STAGE_BY_VALUE = STAGES.reduce((m, s) => ({ ...m, [s.value]: s }), {});

const REGIONS = [
  { key: 'all',                     label: 'All' },
  { key: 'East North Central',      label: 'E. North Central' },
  { key: 'West North Central',      label: 'W. North Central' },
  { key: 'South',                   label: 'South' },
  { key: 'West',                    label: 'West' },
];

const PRIORITY_BADGE = {
  3: { className: 'bg-emerald-500/20 text-emerald-300', label: '★★★ Priority' },
  2: { className: 'bg-white/10 text-white/70',           label: '★★ Outreach' },
  1: { className: 'bg-blue-500/20 text-blue-300',        label: '★ Paying' },
  0: { className: 'bg-white/5 text-white/50',            label: '—' },
};

const formatMoney = (n) =>
  typeof n === 'number' ? `$${n.toLocaleString()}` : '—';

// ── KPI strip ──────────────────────────────────────────────────────────────

const KpiStrip = ({ summary }) => {
  if (!summary) return null;
  const cards = [
    { label: 'Total cities',     value: summary.total },
    { label: 'Letters sent',     value: summary.by_status?.letter_sent || 0 },
    { label: 'Enquiries in',     value: summary.by_status?.enquiry_received || 0 },
    { label: 'In discussion',    value: summary.by_status?.in_discussion || 0 },
    { label: 'Active',           value: summary.by_status?.active || 0 },
    { label: 'Pipeline value',   value: formatMoney(summary.pipeline_value_usd || 0) },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="outreach-kpi-strip">
      {cards.map((c) => (
        <div key={c.label} className="bg-white/5 border border-white/10 rounded-xl p-3">
          <p className="text-white/60 text-xs">{c.label}</p>
          <p className="text-2xl font-semibold text-white">{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ── Edit modal ─────────────────────────────────────────────────────────────

const EditModal = ({ target, onClose, onSaved }) => {
  const [form, setForm] = useState({
    status:           target.status || 'not_contacted',
    outreach_email:   target.outreach_email || '',
    linkedin_url:     target.linkedin_url || '',
    letter_sent_date: target.letter_sent_date || '',
    response_date:    target.response_date || '',
    next_action:      target.next_action || '',
    next_action_due:  target.next_action_due || '',
    gift_value_usd:   target.gift_value_usd ?? '',
    crm_notes:        target.crm_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      payload.gift_value_usd = payload.gift_value_usd === '' ? null : Number(payload.gift_value_usd);
      const res = await apiClient.request(`/api/admin/outreach/targets/${target.target_id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      onSaved(res.target);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="outreach-edit-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl text-white max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">{target.city}, {target.state}</h3>
            <p className="text-white/60 text-xs">
              Mayor {target.mayor_name} · {target.region} · {PRIORITY_BADGE[target.priority_stars]?.label}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="outreach-edit-close">
            <X size={18} />
          </button>
        </div>

        {target.notes && (
          <p className="text-xs text-white/70 italic bg-white/5 rounded-lg p-2 mb-3">
            Source notes: {target.notes}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">
            <span className="text-xs text-white/70 mb-1 block">Status</span>
            <select
              value={form.status}
              onChange={setField('status')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-status"
            >
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-xs text-white/70 mb-1 block">Mayor email</span>
            <input
              type="email"
              value={form.outreach_email}
              onChange={setField('outreach_email')}
              placeholder="mayor@cityname.gov"
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-email"
            />
          </label>
          <label>
            <span className="text-xs text-white/70 mb-1 block">LinkedIn URL</span>
            <input
              type="url"
              value={form.linkedin_url}
              onChange={setField('linkedin_url')}
              placeholder="https://linkedin.com/in/…"
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-linkedin"
            />
          </label>

          <label>
            <span className="text-xs text-white/70 mb-1 block">Letter sent</span>
            <input
              type="date"
              value={form.letter_sent_date}
              onChange={setField('letter_sent_date')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-letter-date"
            />
          </label>
          <label>
            <span className="text-xs text-white/70 mb-1 block">Response received</span>
            <input
              type="date"
              value={form.response_date}
              onChange={setField('response_date')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-response-date"
            />
          </label>

          <label className="col-span-2">
            <span className="text-xs text-white/70 mb-1 block">Next action</span>
            <input
              value={form.next_action}
              onChange={setField('next_action')}
              placeholder="e.g. Follow-up call with deputy"
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-next-action"
            />
          </label>
          <label>
            <span className="text-xs text-white/70 mb-1 block">Next action due</span>
            <input
              type="date"
              value={form.next_action_due}
              onChange={setField('next_action_due')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-next-due"
            />
          </label>
          <label>
            <span className="text-xs text-white/70 mb-1 block">Gift value (USD)</span>
            <input
              type="number"
              min={0}
              value={form.gift_value_usd}
              onChange={setField('gift_value_usd')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-gift-value"
            />
          </label>

          <label className="col-span-2">
            <span className="text-xs text-white/70 mb-1 block">CRM notes</span>
            <textarea
              rows={3}
              value={form.crm_notes}
              onChange={setField('crm_notes')}
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
              data-testid="outreach-edit-notes"
            />
          </label>
        </div>

        {error && <p className="text-rose-400 text-xs mt-2">{error}</p>}

        <div className="flex gap-2 justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/10 text-sm"
            data-testid="outreach-edit-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm flex items-center gap-1 disabled:opacity-50"
            data-testid="outreach-edit-save"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Send Letter modal (Postmark mail-merge) ────────────────────────────────
//
// `kind` controls which preview + send endpoints are used:
//   "invitation" → /letter-preview, /send-letter
//   "followup"   → /followup-preview, /send-followup
//
// In both cases the admin can edit the rendered subject + HTML body before
// hitting "Send via Postmark".

const SendLetterModal = ({ target, kind = 'invitation', onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [toEmail, setToEmail] = useState(target.outreach_email || '');
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState(null);

  const previewPath = kind === 'followup' ? 'followup-preview' : 'letter-preview';
  const sendPath    = kind === 'followup' ? 'send-followup'    : 'send-letter';
  const headlineVerb = kind === 'followup' ? 'Send follow-up' : 'Send invitation';

  useEffect(() => {
    apiClient.request(`/api/admin/outreach/targets/${target.target_id}/${previewPath}`)
      .then((p) => {
        setPreview(p);
        setSubject(p.subject);
        setBody(p.html_body);
        if (!toEmail && p.to_email) setToEmail(p.to_email);
      })
      .catch((e) => setError(e.message || 'Could not load letter preview'));
    // Verified target-and-kind-only effect. `previewPath` is derived from
    // `kind` (already listed) so re-listing it would be redundant. `toEmail`
    // is used ONLY as a one-time "prefill if empty" guard — including it
    // would cause a redundant preview refetch every time the operator
    // edits the email field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.target_id, kind]);

  const send = async () => {
    if (!toEmail || !toEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await apiClient.request(
        `/api/admin/outreach/targets/${target.target_id}/${sendPath}`,
        {
          method: 'POST',
          body: JSON.stringify({ to_email: toEmail, subject, html_body: body }),
        }
      );
      if (res.status === 'sent') {
        onSent(res.target);
      } else {
        setError(res.delivery?.error || 'Postmark reported a failure.');
      }
    } catch (e) {
      setError(e.message || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="outreach-send-letter-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-3xl text-white max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">
              {kind === 'followup' ? 'Send follow-up' : 'Send invitation'} — {target.city}, {target.state}
            </h3>
            <p className="text-white/60 text-xs">
              Mayor {target.mayor_name} · Mail-merge via Postmark ·{' '}
              {kind === 'followup'
                ? <>Idle <strong>{preview?.days_idle ?? '—'} days</strong> since the original letter.</>
                : <>Status will auto-advance to <strong>Letter Sent</strong>.</>}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="outreach-letter-close">
            <X size={18} />
          </button>
        </div>

        {!preview ? (
          <div className="text-white/60 text-sm flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Rendering personalised letter…
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <label>
              <span className="text-xs text-white/70 mb-1 block">Mayor email</span>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="mayor@cityname.gov"
                className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
                data-testid="outreach-letter-to-email"
              />
            </label>
            <label>
              <span className="text-xs text-white/70 mb-1 block">Subject</span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10"
                data-testid="outreach-letter-subject"
              />
            </label>
            <label>
              <span className="text-xs text-white/70 mb-1 block">Body (HTML — edit before sending)</span>
              <textarea
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10 font-mono text-xs leading-relaxed"
                data-testid="outreach-letter-body"
              />
            </label>
            <div>
              <p className="text-xs text-white/70 mb-1">Preview</p>
              <div
                className="rounded-xl bg-white text-gray-900 p-4 max-h-72 overflow-y-auto text-sm"
                dangerouslySetInnerHTML={{ __html: body }}
              />
            </div>
          </div>
        )}

        {error && <p className="text-rose-400 text-xs mt-2" data-testid="outreach-letter-error">{error}</p>}

        <div className="flex gap-2 justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/10 text-sm"
            data-testid="outreach-letter-cancel"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending || !preview}
            className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm flex items-center gap-1 disabled:opacity-50"
            data-testid="outreach-letter-send"
          >
            {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {sending ? 'Sending…' : `${headlineVerb} via Postmark`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Market Report PDF + notes modal ────────────────────────────────────────

const MarketReportModal = ({ onClose }) => {
  const [notes, setNotes] = useState('');
  const [meta, setMeta]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [pdfUrl, setPdfUrl]   = useState(null);

  useEffect(() => {
    apiClient.request('/api/admin/outreach/market-report/notes')
      .then((d) => { setNotes(d.notes || ''); setMeta(d); })
      .catch(() => setNotes(''));

    // Fetch the PDF as a blob using the auth header so we can render
    // it in an <iframe> without a public token in the URL. We capture the
    // freshly-created object URL in `createdUrl` local so the cleanup can
    // revoke THIS effect's blob, not whichever `pdfUrl` state happens to
    // be current when the component later unmounts.
    let createdUrl = null;
    const token = localStorage.getItem('hibff_auth_token');
    fetch(`${apiClient.baseUrl}/api/admin/outreach/market-report`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('Failed to load PDF'))))
      .then((blob) => {
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl(createdUrl);
      })
      .catch(() => setPdfUrl(null));

    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiClient.request('/api/admin/outreach/market-report/notes', {
        method: 'PUT',
        body: JSON.stringify({ notes }),
      });
      setSavedAt(r.updated_at);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-3 py-6"
      onClick={onClose}
      data-testid="outreach-market-report-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-5xl text-white flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold">HiBFF Schools Market Report — Huge Potential</h3>
            <p className="text-white/60 text-xs">
              Strategy intelligence on the 60-city pipeline. Last edited{' '}
              {meta?.updated_at ? new Date(meta.updated_at).toLocaleString() : '—'}{' '}
              {meta?.updated_by ? `by ${meta.updated_by}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="outreach-market-report-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="rounded-xl overflow-hidden border border-white/10 bg-black h-[60vh]">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="HiBFF Schools Market Report"
                className="w-full h-full"
                data-testid="outreach-market-report-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/60 text-sm gap-2">
                <Loader2 className="animate-spin" size={14} /> Loading PDF…
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Your notes on this report</label>
              {savedAt && (
                <span className="text-xs text-emerald-400">
                  Saved {new Date(savedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
            <textarea
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Jot any observations, follow-ups, or decisions taken from this report…"
              className="w-full px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-sm"
              data-testid="outreach-market-report-notes"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/10 text-sm"
            data-testid="outreach-market-report-close-bottom"
          >
            Close
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm flex items-center gap-1 disabled:opacity-50"
            data-testid="outreach-market-report-save"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save notes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Follow-up queue drawer ────────────────────────────────────────────────
//
// Shows letter_sent rows that have been idle ≥7 days with no reply.
// Clicking a candidate opens the SendLetterModal in followup mode so the
// admin can review & edit the auto-rendered follow-up before it goes out.

const FollowupQueueModal = ({ onClose, onPick }) => {
  const [queue, setQueue] = useState(null);
  const [meta, setMeta]   = useState(null);

  const load = () => {
    apiClient.request('/api/admin/outreach/followup-queue')
      .then((d) => { setQueue(d.queue || []); setMeta(d); })
      .catch(() => setQueue([]));
  };

  useEffect(() => { load(); }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="outreach-followup-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-3xl text-white max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold">Follow-up queue</h3>
            <p className="text-white/60 text-xs">
              Mayors who haven&apos;t replied within{' '}
              <strong>{meta?.due_after_days ?? 7} days</strong> of the original letter.
              Click any row to review & edit the follow-up before it goes out.
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="outreach-followup-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {queue === null ? (
            <div className="text-white/60 text-sm flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Loading queue…
            </div>
          ) : queue.length === 0 ? (
            <p className="text-white/60 text-sm">
              No follow-ups due. Once a mayor sits in <strong>Letter Sent</strong> for 7 days
              without a reply, they&apos;ll appear here for your review.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {queue.map((t) => (
                <li
                  key={t.target_id}
                  className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 cursor-pointer"
                  onClick={() => onPick(t)}
                  data-testid={`outreach-followup-pick-${t.target_id}`}
                >
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 whitespace-nowrap">
                    {t.days_idle}d idle
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 truncate">
                      <strong>{t.city}, {t.state}</strong>
                      <span className="text-white/60"> · {t.mayor_name}</span>
                    </p>
                    <p className="text-xs text-white/50 truncate">
                      {t.outreach_email || 'no email on file'} ·{' '}
                      {t.first_opened_at ? 'Opened original letter' : 'No open recorded'}
                      {t.last_followup_at ? ` · Last follow-up ${new Date(t.last_followup_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-emerald-400 whitespace-nowrap">Review →</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Activity / webhook events drawer ───────────────────────────────────────

const EVENT_LABELS = {
  delivery:       { label: 'Delivered',      className: 'bg-emerald-500/10 text-emerald-300' },
  open:           { label: 'Opened',         className: 'bg-sky-500/10 text-sky-300' },
  click:          { label: 'Link clicked',   className: 'bg-violet-500/10 text-violet-300' },
  bounce:         { label: 'Bounced',        className: 'bg-amber-500/10 text-amber-300' },
  spamcomplaint:  { label: 'Spam complaint', className: 'bg-rose-500/10 text-rose-300' },
  inbound:        { label: 'Mayor reply',    className: 'bg-orange-500/10 text-orange-300' },
  inboundemail:   { label: 'Mayor reply',    className: 'bg-orange-500/10 text-orange-300' },
};

const EventsModal = ({ onClose }) => {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    apiClient.request('/api/admin/outreach/events?limit=100')
      .then((d) => setEvents(d.events || []))
      .catch(() => setEvents([]));
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="outreach-events-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-3xl text-white max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold">Postmark activity feed</h3>
            <p className="text-white/60 text-xs">
              Mayor replies, bounces, opens and clicks — auto-fed by the Postmark webhook.
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="outreach-events-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {events === null ? (
            <div className="text-white/60 text-sm flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Loading activity…
            </div>
          ) : events.length === 0 ? (
            <p className="text-white/60 text-sm">
              No webhook events yet. Configure Postmark to POST to{' '}
              <code className="text-orange-300">/api/public/outreach/postmark-webhook?token=…</code>{' '}
              with the secret from <code>POSTMARK_WEBHOOK_SECRET</code>.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {events.map((ev, i) => {
                const meta = EVENT_LABELS[ev.record_type] || { label: ev.record_type, className: 'bg-white/10 text-white/70' };
                return (
                  <li key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>
                      {meta.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/90 truncate">
                        {ev.matched_city ? <strong>{ev.matched_city}</strong> : <em className="text-white/50">unmatched</em>}
                        {ev.email ? <span className="text-white/60"> · {ev.email}</span> : null}
                      </p>
                      {ev.subject && <p className="text-xs text-white/50 truncate">{ev.subject}</p>}
                      {ev.type_detail && <p className="text-[10px] text-white/40">{ev.type_detail}</p>}
                    </div>
                    <span className="text-xs text-white/50 whitespace-nowrap">
                      {ev.received_at ? new Date(ev.received_at).toLocaleString() : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main tracker view ──────────────────────────────────────────────────────

const OutreachTracker = () => {
  const [targets, setTargets]   = useState(null);
  const [summary, setSummary]   = useState(null);
  const [region, setRegion]     = useState('all');
  const [priority, setPriority] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing]   = useState(null);
  const [sendingTo, setSendingTo]   = useState(null);
  const [sendKind, setSendKind]     = useState('invitation');
  const [showReport, setShowReport] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showFollowups, setShowFollowups] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  // Inline status quick-change — fires the same PATCH endpoint the Edit
  // modal uses, optimistically updates the row + KPI strip so the
  // pipeline movement is reflected without a full page round-trip.
  const [statusSaving, setStatusSaving] = useState(null);

  // Wrapped in useCallback so the effect below can list it in deps without
  // re-firing on every parent re-render. Reference changes ONLY when the
  // filters (region / priority / statusFilter) change.
  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (region !== 'all')       params.append('region', region);
    if (priority !== 'all')     params.append('priority', priority);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    const qs = params.toString() ? `?${params.toString()}` : '';
    try {
      const [list, sum] = await Promise.all([
        apiClient.request(`/api/admin/outreach/targets${qs}`),
        apiClient.request('/api/admin/outreach/summary'),
      ]);
      setTargets(list.targets || []);
      setSummary(sum);
    } catch (_e) {
      setTargets([]);
    }
  }, [region, priority, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const reseed = async () => {
    setReseeding(true);
    try {
      await apiClient.request('/api/admin/outreach/seed', { method: 'POST' });
      await load();
    } finally {
      setReseeding(false);
    }
  };

  const onSaved = (updated) => {
    setEditing(null);
    setTargets((prev) => prev.map((t) => (t.target_id === updated.target_id ? updated : t)));
    // Refresh KPI strip
    apiClient.request('/api/admin/outreach/summary').then(setSummary).catch(() => {});
  };

  // Inline status quick-change — fires the same PATCH endpoint the Edit
  // modal uses, optimistically updates the row + KPI strip so the
  // pipeline movement is reflected without a full page round-trip.
  const quickStatusChange = async (targetId, nextStatus, currentStatus) => {
    if (nextStatus === currentStatus) return;
    setStatusSaving(targetId);
    // Optimistic patch
    setTargets((prev) => prev.map((t) => (t.target_id === targetId ? { ...t, status: nextStatus } : t)));
    try {
      const res = await apiClient.request(`/api/admin/outreach/targets/${targetId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      if (res?.target) {
        setTargets((prev) => prev.map((t) => (t.target_id === targetId ? res.target : t)));
      }
      apiClient.request('/api/admin/outreach/summary').then(setSummary).catch(() => {});
    } catch (_e) {
      // Revert on failure
      setTargets((prev) => prev.map((t) => (t.target_id === targetId ? { ...t, status: currentStatus } : t)));
    } finally {
      setStatusSaving(null);
    }
  };

  const stateOptions = useMemo(() => {
    if (!targets) return [];
    return [...new Set(targets.map((t) => t.state))].sort();
  }, [targets]);

  if (targets === null) {
    return <div className="text-white/60"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="schools-outreach-tracker">
      <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="text-white text-lg font-semibold">Mayor Outreach Tracker</h2>
          <p className="text-white/60 text-xs">
            60 cities · 4 census regions · pipelines from spreadsheet master.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFollowups(true)}
            className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-200 text-xs flex items-center gap-1 hover:bg-amber-500/30"
            data-testid="outreach-open-followups"
          >
            <Clock size={14} />
            Follow-up queue
          </button>
          <button
            onClick={() => setShowEvents(true)}
            className="px-3 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 text-xs flex items-center gap-1 hover:bg-emerald-500/30"
            data-testid="outreach-open-events"
          >
            <Inbox size={14} />
            Activity feed
          </button>
          <button
            onClick={() => setShowReport(true)}
            className="px-3 py-2 rounded-lg bg-orange-500/20 text-orange-200 text-xs flex items-center gap-1 hover:bg-orange-500/30"
            data-testid="outreach-open-market-report"
          >
            <FileText size={14} />
            Market report
          </button>
          <button
            onClick={reseed}
            disabled={reseeding}
            className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs flex items-center gap-1 hover:bg-white/15 disabled:opacity-50"
            data-testid="outreach-reseed-button"
          >
            {reseeding ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            Re-seed from spreadsheet
          </button>
        </div>
      </div>

      <KpiStrip summary={summary} />

      <div className="flex flex-wrap gap-2 mb-3" data-testid="outreach-region-chips">
        {REGIONS.map((r) => (
          <button
            key={r.key}
            onClick={() => setRegion(r.key)}
            className={`px-3 py-1.5 rounded-full text-xs ${
              region === r.key ? 'bg-orange-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
            data-testid={`outreach-region-${r.key.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {r.label}
          </button>
        ))}
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="ml-auto px-3 py-1.5 rounded-full text-xs bg-white/5 text-white/80 border border-white/10"
          data-testid="outreach-priority-filter"
        >
          <option value="all">All priorities</option>
          <option value="3">★★★ Gives</option>
          <option value="2">★★ Outreach</option>
          <option value="1">★ Paying</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-full text-xs bg-white/5 text-white/80 border border-white/10"
          data-testid="outreach-status-filter"
        >
          <option value="all">All statuses</option>
          {STAGES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {targets.length === 0 ? (
        <p className="text-white/60 text-sm bg-white/5 rounded-xl p-4">
          No targets match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm text-white/90">
            <thead className="bg-white/5 text-white/60 text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Region / State</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">Mayor</th>
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2 text-right">Population</th>
                <th className="px-3 py-2 text-right">Poverty</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Next action</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => {
                const stage = STAGE_BY_VALUE[t.status] || STAGE_BY_VALUE.not_contacted;
                const prio  = PRIORITY_BADGE[t.priority_stars] || PRIORITY_BADGE[0];
                return (
                  <tr key={t.target_id} className="border-t border-white/10 hover:bg-white/5" data-testid={`outreach-row-${t.target_id}`}>
                    <td className="px-3 py-2 text-xs text-white/70">
                      <div>{t.region}</div>
                      <div className="text-white/50">{t.state}</div>
                    </td>
                    <td className="px-3 py-2 font-medium">{t.city}</td>
                    <td className="px-3 py-2">
                      {t.mayor_name}
                      {t.linkedin_url && (
                        <a
                          href={t.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-1 text-orange-400"
                          data-testid={`outreach-linkedin-${t.target_id}`}
                        >
                          <ExternalLink size={12} className="inline" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${prio.className}`}>
                        {prio.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-white/70">
                      {t.population ? t.population.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-white/70">
                      {t.poverty_rate_pct != null ? `${t.poverty_rate_pct}%` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="relative inline-block">
                        <select
                          value={t.status || 'not_contacted'}
                          onChange={(e) => quickStatusChange(t.target_id, e.target.value, t.status)}
                          disabled={statusSaving === t.target_id}
                          className={`text-xs px-2 py-0.5 rounded-full ${stage.pillClass} appearance-none cursor-pointer pr-6 border-0 outline-none focus:ring-1 focus:ring-white/30`}
                          style={{ backgroundPosition: 'right 0.25rem center' }}
                          data-testid={`outreach-status-quick-${t.target_id}`}
                          aria-label="Change pipeline status"
                        >
                          {STAGES.map((s) => (
                            <option key={s.value} value={s.value} className="bg-slate-900 text-white">{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-white/70 max-w-[180px] truncate">
                      {t.next_action || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => { setSendKind('invitation'); setSendingTo(t); }}
                          className="text-xs text-emerald-400 hover:underline flex items-center gap-1"
                          title="Send invitation via Postmark"
                          data-testid={`outreach-send-${t.target_id}`}
                        >
                          <Send size={12} /> Send
                        </button>
                        <button
                          onClick={() => setEditing(t)}
                          className="text-xs text-orange-400 hover:underline"
                          data-testid={`outreach-edit-${t.target_id}`}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* unused but kept for table-state UX continuity */}
      {stateOptions.length === 0 && null}

      {editing && (
        <EditModal
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      {sendingTo && (
        <SendLetterModal
          target={sendingTo}
          kind={sendKind}
          onClose={() => setSendingTo(null)}
          onSent={(updated) => {
            setSendingTo(null);
            setTargets((prev) => prev.map((t) => (t.target_id === updated.target_id ? updated : t)));
            apiClient.request('/api/admin/outreach/summary').then(setSummary).catch(() => {});
          }}
        />
      )}

      {showReport && <MarketReportModal onClose={() => setShowReport(false)} />}
      {showEvents && <EventsModal onClose={() => setShowEvents(false)} />}
      {showFollowups && (
        <FollowupQueueModal
          onClose={() => setShowFollowups(false)}
          onPick={(target) => {
            setShowFollowups(false);
            setSendKind('followup');
            setSendingTo(target);
          }}
        />
      )}
    </div>
  );
};

export default OutreachTracker;
